import { QueryClient, MutationCache } from '@tanstack/react-query'
import { toast } from '@/components/ui/Toaster'

// Defaults tuned for a small BD team's traffic pattern:
//   - staleTime 30s so back-and-forth between Pipeline ↔ Dashboard ↔ Rfps
//     doesn't re-hit Lambda; realtime invalidations + window focus refetch
//     keep data fresh when it actually moves.
//   - gcTime 5m matches Anthropic prompt-cache so we don't churn memory.
//   - retry once on 5xx; never on 4xx (auth, validation).
export const queryClient = new QueryClient({
  // Every mutation that doesn't supply its own onError ends here. Pages can
  // still opt out by passing onError in useMutation options.
  mutationCache: new MutationCache({
    onError: (err) => {
      const e = err as { status?: number; message?: string }
      if (e?.status === 400) toast.error('Request rejected', e.message ?? 'Check the fields and try again.')
      else if (e?.status === 401 || e?.status === 403) toast.error('Not authorized')
      else if ((e?.status ?? 0) >= 500) toast.error('Server error', 'Please try again — if it sticks, check the logs.')
      else toast.error('Something went wrong', e?.message)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, err) => {
        const status = (err as { status?: number })?.status ?? 0
        if (status >= 400 && status < 500) return false
        return failureCount < 1
      },
    },
    mutations: {
      // Surfaced by the seed pass: tab B never invalidated after tab A's
      // mutation. Realtime fixes most of this; this is the belt-and-braces.
      retry: 0,
    },
  },
})
